import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface PricingFeature {
  text: string;
  included: boolean;
}

interface PricingCardProps {
  title: string;
  price: string;
  period: string;
  description: string;
  features: PricingFeature[];
  buttonText: string;
  buttonLink: string;
  highlighted?: boolean;
  delay?: number;
}

const PricingCard = ({
  title,
  price,
  period,
  description,
  features,
  buttonText,
  buttonLink,
  highlighted = false,
  delay = 0,
}: PricingCardProps) => {
  const cardClasses = highlighted
    ? 'card border-primary relative z-10 scale-105 shadow-xl'
    : 'card border border-neutral-200';

  const buttonClasses = highlighted
    ? 'btn bg-primary text-white hover:bg-primary-dark w-full'
    : 'btn border-2 border-primary text-primary hover:bg-primary hover:text-white w-full';

  return (
    <motion.div 
      className={cardClasses}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true, margin: "-100px" }}
    >
      {highlighted && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center">
          <span className="bg-primary-dark text-white py-1 px-4 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}
      
      <h3 className="font-sans font-bold text-xl mb-4">{title}</h3>
      
      <div className="mb-4">
        <span className="font-sans font-bold text-3xl">{price}</span>
        <span className="text-neutral-500">/{period}</span>
      </div>
      
      <p className="text-neutral-600 mb-6">{description}</p>
      
      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <span className={`mt-1 ${feature.included ? 'text-success' : 'text-neutral-400'}`}>
              <Check size={16} />
            </span>
            <span className={feature.included ? 'text-neutral-800' : 'text-neutral-500 line-through'}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>
      
      <a href={buttonLink} className={buttonClasses}>
        {buttonText}
      </a>
    </motion.div>
  );
};

export default PricingCard;